<template>
  <v-container fluid class="detail-page">
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="48" />
      <span>Cargando pedido...</span>
    </div>

    <template v-else-if="order">
      <div class="page-header">
        <div>
          <div class="eyebrow">Reposición interna</div>
          <div class="title-row">
            <h1>Pedido interno</h1>
            <v-chip small :color="getStatusColor(order.status)" text-color="white">
              {{ getStatusLabel(order.status) }}
            </v-chip>
          </div>
          <p>Creado el {{ formatDateTime(order.createdAt) }} por {{ order.createdBy }}</p>
        </div>
        <div class="header-actions">
          <v-btn text @click="goBack">
            <v-icon left>mdi-arrow-left</v-icon>
            Volver
          </v-btn>
          <v-btn v-if="canEdit" color="primary" outlined @click="editOrder">
            <v-icon left>mdi-pencil-outline</v-icon>
            Editar
          </v-btn>
          <v-btn v-if="canConvert" color="success" @click="openConvertDialog">
            <v-icon left>mdi-check-decagram-outline</v-icon>
            Aprobar y crear OC
          </v-btn>
        </div>
      </div>

      <v-row>
        <v-col cols="12" md="4">
          <v-card flat class="section-panel mb-4">
            <v-card-title>
              <v-icon left color="primary">mdi-information-outline</v-icon>
              Información
            </v-card-title>
            <v-card-text>
              <div class="info-stack">
                <div class="info-row">
                  <span>Proveedor</span>
                  <strong>{{ order.provider?.name || "-" }}</strong>
                  <small>Código: {{ order.provider?.code || "-" }}</small>
                </div>
                <div class="info-row">
                  <span>Fecha del pedido</span>
                  <strong>{{ formatDate(order.orderDate) }}</strong>
                </div>
                <div v-if="order.notes" class="info-row">
                  <span>Notas</span>
                  <strong>{{ order.notes }}</strong>
                </div>
                <div v-if="order.convertedPurchase" class="info-row">
                  <span>Orden de compra generada</span>
                  <v-btn text small color="success" class="px-0 purchase-link" @click="goToPurchase(order.convertedPurchase.id)">
                    {{ order.convertedPurchase.docNumber }}
                    <v-icon right small>mdi-open-in-new</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-card flat class="section-panel">
            <v-card-title>
              <v-icon left color="primary">mdi-history</v-icon>
              Historial
            </v-card-title>
            <v-card-text>
              <div class="timeline-list">
                <div class="timeline-item">
                  <span class="timeline-dot pending"></span>
                  <div>
                    <strong>Pedido creado</strong>
                    <small>{{ formatDateTime(order.createdAt) }}</small>
                  </div>
                </div>
                <div v-if="order.status === 'completed' || order.status === 'cancelled'" class="timeline-item">
                  <span class="timeline-dot" :class="order.status"></span>
                  <div>
                    <strong>{{ order.status === "completed" ? "Convertido a OC" : "Pedido cancelado" }}</strong>
                    <small>{{ formatDateTime(order.updatedAt) }}</small>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card flat class="section-panel items-panel">
            <v-card-title class="items-title">
              <div>
                <v-icon left color="primary">mdi-package-variant</v-icon>
                Ítems del pedido
              </div>
              <v-chip small outlined color="primary">
                {{ order.items?.length || 0 }} ítem{{ (order.items?.length || 0) !== 1 ? "s" : "" }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table class="items-table">
                <thead>
                  <tr>
                    <th class="text-left">Código</th>
                    <th class="text-left">Descripción</th>
                    <th class="text-right">Cantidad</th>
                    <th class="text-left">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in order.items" :key="item.id">
                    <td><span class="code-pill">{{ item.product?.code || "-" }}</span></td>
                    <td>
                      <div class="item-name">{{ item.product?.name || item.product?.description || "-" }}</div>
                      <div v-if="item.product?.brand" class="item-meta">{{ item.product.brand }}</div>
                    </td>
                    <td class="text-right item-quantity">{{ formatQuantity(item.quantity) }}</td>
                    <td class="item-note">{{ item.notes || "-" }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="convertDialog" max-width="560" persistent>
      <v-card v-if="order" class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon left color="white">mdi-check-decagram-outline</v-icon>
          Aprobar solicitud
        </v-card-title>
        <v-card-text class="pt-5">
          <v-alert type="info" text dense class="mb-4">
            Se va a crear una Orden de Compra formal para <strong>{{ order.provider?.name }}</strong>.
          </v-alert>
          <v-form ref="convertForm" v-model="convertFormValid">
            <v-select
              v-model="convertData.warehouseId"
              :items="warehouses"
              item-text="name"
              item-value="id"
              label="Depósito de destino"
              outlined
              dense
              required
              :rules="[v => !!v || 'Seleccione un depósito']"
            />
            <v-textarea
              v-model="convertData.notes"
              label="Notas adicionales"
              outlined
              rows="2"
              dense
            />
            <div class="dialog-hint">
              Este pedido contiene {{ order.items?.length || 0 }} ítem(s) que se transferirán a la nueva OC.
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="converting" @click="convertDialog = false">Cancelar</v-btn>
          <v-btn color="success" :loading="converting" :disabled="!convertFormValid" @click="convertToPurchase">
            <v-icon left>mdi-check</v-icon>
            Aprobar y crear OC
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="4000"
      top
      right
      :class="[`module-toast`, `module-toast--${snackbar.color}`]"
      content-class="white--text font-weight-bold"
    >
      {{ snackbar.message }}
      <template v-slot:action>
        <v-btn text color="white" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>
<script>
import { getOrder, approveOrder, generateIdempotencyKey } from "@/services/internalOrders";
import { getWarehouses } from "@/services/warehouses";
import { mapGetters } from "vuex";
export default {
  name: "InternalOrderDetail",
  data() {
    return { loading: true, order: null, warehouses: [], convertDialog: false, converting: false, convertFormValid: false, convertData: { warehouseId: null, notes: "" }, snackbar: { show: false, message: "", color: "success" } };
  },
  computed: {
    ...mapGetters("auth", ["user", "hasPermission"]),
    canEdit() { if (!this.order) return false; if (this.order.status !== "pending") return false; return this.order.createdBy === this.user?.email || this.order.createdBy === this.user?.username || this.hasPermission("internal_orders.manage"); },
    canConvert() { if (!this.order) return false; if (this.order.status !== "pending") return false; return this.hasPermission("internal_orders.manage"); },
  },
  mounted() { this.loadOrder(); this.loadWarehouses(); },
  methods: {
    async loadOrder() { this.loading = true; try { const response = await getOrder(this.$route.params.id); if (response.ok) { this.order = response.data; } else { this.showError("Error al cargar el pedido"); this.goBack(); } } catch (error) { this.showError("Error al cargar el pedido"); this.goBack(); } finally { this.loading = false; } },
    async loadWarehouses() { try { const response = await getWarehouses(); if (response.ok) { this.warehouses = response.data || []; } } catch (error) { console.error("Error loading warehouses:", error); } },
    getStatusColor(status) { return { pending: "warning", completed: "success", cancelled: "error" }[status] || "grey"; },
    getStatusLabel(status) { return { pending: "Pendiente", completed: "Completado", cancelled: "Cancelado" }[status] || status; },
    formatDate(dateStr) { return dateStr ? new Date(dateStr).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"; },
    formatDateTime(dateStr) { return dateStr ? new Date(dateStr).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"; },
    formatQuantity(value) { return new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(value || 0)); },
    goBack() { this.$router.push("/pedidos-internos"); },
    editOrder() { this.$router.push(`/pedidos-internos/${this.order.id}/editar`); },
    goToPurchase(purchaseId) { this.$router.push(`/compras/${purchaseId}`); },
    openConvertDialog() { this.convertDialog = true; this.convertData = { warehouseId: this.warehouses[0]?.id || null, notes: "" }; },
    async convertToPurchase() { if (!this.$refs.convertForm.validate()) return; this.converting = true; try { const idempotencyKey = generateIdempotencyKey("approve"); const response = await approveOrder(this.order.id, this.convertData, idempotencyKey); if (response.ok) { this.showSuccess(response.data?.message || "Solicitud aprobada"); this.convertDialog = false; this.loadOrder(); } else { this.showError(response.error || "Error al aprobar"); } } catch (error) { this.showError("Error al aprobar la solicitud"); } finally { this.converting = false; } },
    showSuccess(message) { this.snackbar = { show: true, message, color: "success" }; },
    showError(message) { this.snackbar = { show: true, message, color: "error" }; },
  },
};
</script>
<style scoped>
.detail-page {
  --module-primary: var(--nav-link-active-color, #223b6a);
  --module-action: var(--accent-primary, #3b82f6);
  --module-line: var(--border-color, rgba(26, 35, 51, 0.1));
  --module-muted: var(--text-tertiary, #64748b);
  background: transparent;
  min-height: calc(100vh - 64px);
  padding-top: 12px;
}

.loading-state {
  display: grid;
  place-items: center;
  gap: 12px;
  min-height: 260px;
  color: var(--module-muted);
  font-size: 14px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-header h1 {
  margin: 0;
  color: var(--module-primary);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
}

.page-header p {
  margin: 4px 0 0;
  color: var(--module-muted);
  font-size: 14px;
}

.eyebrow {
  color: var(--module-action);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.section-panel {
  border: 1px solid var(--module-line);
  border-radius: 8px;
  background: var(--bg-secondary, #fff) !important;
}

.section-panel .v-card__title {
  color: var(--text-primary, #1a2333);
  font-size: 14px;
  font-weight: 700;
  min-height: 48px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--module-line);
}

.section-panel .v-card__text {
  padding: 14px 16px;
}

.items-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.info-stack {
  border: 1px solid var(--module-line);
  border-radius: 8px;
  overflow: hidden;
}

.info-row {
  display: grid;
  gap: 3px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--module-line);
}

.info-row:last-child {
  border-bottom: 0;
}

.info-row span,
.timeline-item small,
.item-meta,
.item-note,
.dialog-hint {
  color: var(--module-muted);
  font-size: 12px;
}

.info-row strong,
.timeline-item strong,
.item-name {
  color: var(--text-primary, #1a2333);
  font-size: 13px;
  font-weight: 700;
}

.info-row small {
  color: var(--text-secondary, #475569);
  font-size: 12px;
}

.purchase-link {
  justify-content: flex-start;
  height: 28px !important;
  font-weight: 700;
}

.timeline-list {
  display: grid;
  gap: 12px;
  padding-left: 4px;
}

.timeline-item {
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 10px;
  align-items: start;
}

.timeline-dot {
  width: 10px;
  height: 10px;
  margin-top: 4px;
  border-radius: 999px;
  background: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18);
}

.timeline-dot.pending {
  background: #a16207;
  box-shadow: 0 0 0 3px rgba(161, 98, 7, 0.18);
}

.timeline-dot.completed {
  background: #15803d;
  box-shadow: 0 0 0 3px rgba(21, 128, 61, 0.18);
}

.timeline-dot.cancelled {
  background: #b91c1c;
  box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.18);
}

.items-table th {
  color: var(--text-secondary, #475569) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.items-table td {
  font-size: 13px;
}

.code-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border: 1px solid rgba(59, 130, 246, 0.28);
  border-radius: 6px;
  color: var(--module-primary);
  background: var(--accent-primary-light, rgba(59, 130, 246, 0.14));
  font-size: 11px;
  font-weight: 700;
}

.item-quantity {
  color: var(--text-primary, #1a2333);
  font-weight: 700;
  white-space: nowrap;
}

.dialog-title {
  background: var(--module-primary);
  color: #fff;
}

.dialog-hint {
  margin-top: 8px;
}

@media (max-width: 780px) {
  .page-header,
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions > * {
    width: 100%;
  }

  .title-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
